import { Injectable } from '@angular/core';
import {
  Action,
  Selector,
  SelectorOptions,
  State,
  StateContext,
  createSelector,
} from '@ngxs/store';
import { patch, removeItem } from '@ngxs/store/operators';
import {
  CounterWidgetControllerService,
  CounterWidgetDataEntryEndUserDto,
} from '@osos/shared/api';
import {
  CounterUserEntryPresenterModel,
  CounterWidgetDataEntryPresenterModel,
  DataEntryPresenterModel,
  LoadingIndicator,
  ManagedHttpContext,
} from '@osos/shared/types';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { CounterFactoryService } from '../../factories';
import { CounterUserEntryFactoryService } from '../../factories/counter-factory/counter-user-entry-factory.service';
import { ApiUtilsService } from '../../services';
import { SignOut } from '../auth/auth.actions';
import { CoreCountersActions } from './core-counters.actions';
export interface PresentedCounterEntries {
  count: number;
  presentedEntries: CounterUserEntryPresenterModel[];
}
export interface CountersEntries {
  [key: string]: PresentedCounterEntries;
}

export interface CoreCounterStateModel {
  presentedCounters?: CounterWidgetDataEntryPresenterModel[];
  counterEntries: CountersEntries;
  loading?: LoadingIndicator;
}
const SECONDS = 30;
@State<CoreCounterStateModel>({
  name: 'coreCounters',
  defaults: {
    presentedCounters: [],
    counterEntries: {},
    loading: new LoadingIndicator(),
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class CoreCounterState {
  private notificationSchedulerSubscription: Subscription;

  constructor(
    private counterWidgetService: CounterWidgetControllerService,
    private counterFactoryService: CounterFactoryService,
    private counterEntryFactoryService: CounterUserEntryFactoryService,
    private apiHelper: ApiUtilsService,
  ) {}

  // --------------------------------- SELECTORS ------------------------------

  @Selector([CoreCounterState])
  /**
   * select transitory modules.
   * it's valid to be readed once after trigger TransitoryFetchModules action
   */
  static presentedCounters(
    state: CoreCounterStateModel,
  ): CounterWidgetDataEntryPresenterModel[] {
    return [...state.presentedCounters].sort(
      (a, b) => a.displayOrder - b.displayOrder,
    );
  }

  static presentedCounterEntriesCount(
    counterId: string,
  ): (state: CoreCounterStateModel) => number {
    return createSelector(
      [CoreCounterState],
      (state: CoreCounterStateModel) => state.counterEntries[counterId]?.count,
    );
  }

  static presentedCounterEntries(
    counterId: string,
  ): (state: CoreCounterStateModel) => DataEntryPresenterModel[] {
    return createSelector(
      [CoreCounterState],
      (state: CoreCounterStateModel) =>
        state.counterEntries[counterId]?.presentedEntries,
    );
  }

  @Selector([CoreCounterState])
  static loading(state: CoreCounterStateModel): boolean {
    return state.loading.fetch;
  }

  // --------------------------------- ACTIONS --------------------------------

  @Action(CoreCountersActions.FetchCountersEndUser, { cancelUncompleted: true })
  fetchCounters({
    setState,
    dispatch,
  }: StateContext<CoreCounterStateModel>): Observable<
    CounterWidgetDataEntryPresenterModel[]
  > {
    setState(
      patch<CoreCounterStateModel>({
        presentedCounters: [],
        loading: patch<LoadingIndicator>({
          fetch: true,
        }),
      }),
    );
    return this.counterWidgetService
      .getCounterWidgetsEndUser({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        map((counters) => {
          return counters.map((counter) =>
            this.counterFactoryService.createPresenterModel(counter),
          );
        }),
        tap((counters: CounterWidgetDataEntryPresenterModel[]) => {
          setState(
            patch<CoreCounterStateModel>({
              presentedCounters: counters,
            }),
          );
          dispatch(
            new CoreCountersActions.ScheduleCountersEntriesCountFetching(),
          );
        }),
        finalize(() => {
          setState(
            patch<CoreCounterStateModel>({
              loading: patch<LoadingIndicator>({
                fetch: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(CoreCountersActions.ScheduleCountersEntriesCountFetching)
  scheduleCounterEntriesCountFetching({
    dispatch,
  }: StateContext<CoreCounterStateModel>) {
    // @Todo change this to be websocket or sse later
    // For now we will depend on the interval

    dispatch(new CoreCountersActions.FetchCountersEntriesCount());
    if (this.notificationSchedulerSubscription) return;

    // Schedule the notification
    // TODO: We will stop fetch counters entries count when we have websocket
    // this.notificationSchedulerSubscription = interval(this.countRequestRate)
    //   .pipe(
    //     switchMap(() =>
    //       dispatch(new CoreCountersActions.FetchCountersEntriesCount())
    //     )
    //   )
    //   .subscribe();
  }

  @Action(CoreCountersActions.FetchCountersEntriesCount, {
    cancelUncompleted: true,
  })
  fetchCountersEntriesCount({
    setState,
    getState,
  }: StateContext<CoreCounterStateModel>): Observable<
    { counterId: string; count: number }[]
  > {
    return forkJoin(
      getState().presentedCounters.map((counter) =>
        this.counterWidgetService
          .getCounterWidgetsCountEndUser({
            id: counter.id,
            context: new ManagedHttpContext({
              publicAccessId: counter.view.dataEntry,
            }),
          })
          .pipe(
            map((count) => {
              return { counterId: counter.id, count: count.count };
            }),
          ),
      ),
    ).pipe(
      tap((res: { counterId: string; count: number }[]) => {
        const currentCounterEntries = getState().counterEntries;
        const updatedCounterEntries: CountersEntries = {
          ...res.reduce((acc, curr) => {
            acc[curr.counterId] = {
              count: curr.count,
              presentedEntries:
                currentCounterEntries[curr.counterId]?.presentedEntries,
            } satisfies PresentedCounterEntries;
            return acc;
          }, {}),
        };
        setState(
          patch<CoreCounterStateModel>({
            counterEntries: patch<CountersEntries>(updatedCounterEntries),
          }),
        );
      }),
    );
  }
  @Action(CoreCountersActions.FetchCountersEntries, {
    cancelUncompleted: true,
  })
  fetchCountersEntries(
    { setState }: StateContext<CoreCounterStateModel>,
    { payload }: CoreCountersActions.FetchCountersEntries,
  ) {
    const filter =
      this.apiHelper.prepareFilter<CounterWidgetDataEntryEndUserDto>({
        skip: payload.skip ?? 0,
        limit: payload.limit ?? 20,
      });
    return this.counterWidgetService
      .getCounterWidgetsEntriesEndUser({
        id: payload.counterId,
        filter,
        context: new ManagedHttpContext({
          publicAccessId: payload.viewId,
        }),
      })
      .pipe(
        map((entries) =>
          entries.map((entry) =>
            this.counterEntryFactoryService.createPresenterModel(entry),
          ),
        ),
        tap((entries) => {
          setState(
            patch<CoreCounterStateModel>({
              counterEntries: patch<CountersEntries>({
                [payload.counterId]: patch<PresentedCounterEntries>({
                  presentedEntries: entries,
                }),
              }),
            }),
          );
        }),
      );
  }

  @Action(CoreCountersActions.DeleteCounterEndUser)
  deleteCounter(
    { setState, getState }: StateContext<CoreCounterStateModel>,
    { counterId }: CoreCountersActions.DeleteCounterEndUser,
  ) {
    const counter = getState().presentedCounters.find(
      (counter) => counter.id === counterId,
    );
    if (counter) {
      setState(
        patch<CoreCounterStateModel>({
          presentedCounters: removeItem((e) => e.id === counterId),
        }),
      );
    }
  }

  @Action(SignOut)
  signOut() {
    this.notificationSchedulerSubscription?.unsubscribe();
  }
}
